import type { BookingRow, BookingStatus } from "design-components";

export interface DashboardStat {
  title: string;
  value: number;
  subtitle?: string;
}

/** Single capacity row (Figma: Wheeled / Stack). */
export interface DashboardCapacityMetric {
  id: string;
  label: string;
  filled: number;
  total: number;
  /** 0–100 */
  percent: number;
  variant: "success" | "warning";
}

/** One cell in the booking overview 2×2 grid. */
export interface DashboardOverviewCell {
  status: BookingStatus;
  count: number;
  label: string;
  icon: "warning" | "info" | "success" | "neutral";
}

export const DASHBOARD_CAPACITY_METRICS: DashboardCapacityMetric[] = [
  {
    id: "wheeled",
    label: "Wheeled",
    filled: 10,
    total: 25,
    percent: 40,
    variant: "success",
  },
  {
    id: "stack",
    label: "Stack",
    filled: 43,
    total: 50,
    percent: 87,
    variant: "warning",
  },
];

/** Default overview counts when there are no booking rows yet (Figma demo). */
export const DASHBOARD_OVERVIEW_FALLBACK: Record<BookingStatus, number> = {
  DRAFT: 0,
  UNASSIGNED: 3,
  PENDING: 4,
  CONFIRMED: 2,
  COMPLETE: 4,
  // Operator dashboard overview is a 4-cell Figma grid (no Deleted / In Progress cells).
  // These entries exist only to satisfy `Record<BookingStatus, number>` now that the
  // BookingStatus union covers the full BE ApiBookingStatus surface.
  IN_PROGRESS: 0,
  NO_SHOW: 0,
  DELETED: 0,
};

export const DASHBOARD_PREVIEW_ROW_LIMIT = 8;

/** Figma 811:23411 booking table demo when the API returns no rows (empty state). */
export const DASHBOARD_MOCK_PREVIEW_ROWS: BookingRow[] = [
  {
    customerId: 0,
    id: "figma-1",
    bookingNumber: "BK20058291",
    bookingType: "pickup",
    operatorName: "",
    carrierName: "",
    driverName: "",
    customerName: "Walmart",
    customerStatus: "Active",
    locationLabel: "",
    status: "UNASSIGNED",
    scheduledAt: "2026-04-27T10:00:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-27T10:00:00.000Z",
    shortId: "3909102",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-2",
    bookingNumber: "BK40029113",
    bookingType: "dropoff",
    operatorName: "",
    carrierName: "Express Lanes LLC",
    driverName: "",
    customerName: "Bob & Betty's Food",
    customerStatus: "Active",
    locationLabel: "",
    status: "PENDING",
    scheduledAt: "2026-04-27T11:00:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-27T11:00:00.000Z",
    shortId: "594939",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-3",
    bookingNumber: "BK30044102",
    bookingType: "pickup",
    operatorName: "",
    carrierName: "JR Transport Services",
    driverName: "",
    customerName: "Petsmart",
    customerStatus: "Active",
    locationLabel: "",
    status: "CONFIRMED",
    scheduledAt: "2026-04-28T09:30:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-28T09:30:00.000Z",
    shortId: "4829101",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-4",
    bookingNumber: "BK50011298",
    bookingType: "dropoff",
    operatorName: "",
    carrierName: "",
    driverName: "",
    customerName: "Walmart",
    customerStatus: "Active",
    locationLabel: "",
    status: "UNASSIGNED",
    scheduledAt: "2026-04-28T14:00:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-28T14:00:00.000Z",
    shortId: "2103948",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-5",
    bookingNumber: "BK60088321",
    bookingType: "pickup",
    operatorName: "",
    carrierName: "Express Lanes LLC",
    driverName: "",
    customerName: "Bob & Betty's Food",
    customerStatus: "Active",
    locationLabel: "",
    status: "PENDING",
    scheduledAt: "2026-04-29T08:15:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-29T08:15:00.000Z",
    shortId: "8839210",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-6",
    bookingNumber: "BK70022456",
    bookingType: "dropoff",
    operatorName: "",
    carrierName: "JR Transport Services",
    driverName: "",
    customerName: "Petsmart",
    customerStatus: "Active",
    locationLabel: "",
    status: "CONFIRMED",
    scheduledAt: "2026-04-29T12:45:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-29T12:45:00.000Z",
    shortId: "1029384",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
  {
    customerId: 0,
    id: "figma-7",
    bookingNumber: "BK80055671",
    bookingType: "pickup",
    operatorName: "",
    carrierName: "Express Lanes LLC",
    driverName: "",
    customerName: "Walmart",
    customerStatus: "Active",
    locationLabel: "",
    status: "CONFIRMED",
    scheduledAt: "2026-04-30T07:00:00.000Z",
    mustArriveByAt: "",
    arrivalAt: "2026-04-30T07:00:00.000Z",
    shortId: "5567123",
    shippingLine: "",
    containerNumber: "",
    containerSize: "40'",
    equipmentOwner: "",
    equipmentType: "Container",
    equipType: "Container",
    reefer: false,
    scac: "",
    quantity: 1,
    assignedQuantity: null,
    carrierAssignments: [],
  },
];
